export const predictChartType = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
        return ["Invalid data"];
    }

    const firstItem = data[0];
    const keys = Object.keys(firstItem);

    let hasTimeSeries = keys.some(key => key.toLowerCase().includes("date") || key.toLowerCase().includes("month") || key.toLowerCase().includes("year"));
    let hasCategories = keys.some(key => typeof firstItem[key] === "string");
    let hasNumbers = keys.some(key => typeof firstItem[key] === "number");
    let isHierarchical = data.some(item => item.children);
    let isPercentageData = data.every(item => Object.values(item).some(val => typeof val === "number" && val <= 100));
    let hasMultipleCategories = data.length > 1 && keys.length > 2; // More than one category

    let chartTypes = [];

    if (hasTimeSeries && hasNumbers) {chartTypes.push("Line Chart", "Area Chart");}
    if (hasCategories && hasNumbers) {chartTypes.push("Bar Chart (Vertical and Horizontal)");}
    if (hasCategories && hasNumbers && hasMultipleCategories) {chartTypes.push("Stacked Bar/Column Chart");}
    if (isPercentageData && data.length <= 10) {chartTypes.push("Pie Chart", "Doughnut Chart");}
    if (hasCategories && hasNumbers && keys.length > 3) {chartTypes.push("Radar Chart");}
    if (hasCategories && hasNumbers && keys.some(key => key.toLowerCase().includes("stage") || key.toLowerCase().includes("funnel"))) {chartTypes.push("Funnel Chart");}
    if (data.length > 10 && hasNumbers && keys.length > 3) {chartTypes.push("Heatmap");}
    if (!hasCategories && hasNumbers && keys.length === 2) {chartTypes.push("Scatter Plot");}
    if (isHierarchical) {chartTypes.push("Tree Map");}

    return chartTypes.length > 0 ? chartTypes : ["No suitable chart found"];
};


import ReactECharts from "echarts-for-react";
import React from 'react';
import PropTypes from "prop-types";

const ChartRenderer = ( props ) => {
    const {
  charTitle,
  chartStyle,
  chartType,
  colors,
  data,
  events,
  isScroll,
  isScrollEndLimit,
  isScrollStartLimit,
  labels,
  legendSelection,
  maxiMumObejectLength,
  onChartClick,
  showLabelInside,
  stacked = false,
  toolTipConfig,
  totalValue,
  xAxisData,
  xAxisName,
  yAxisName,
  otherZoomProperty
} = props;

    if (!Array.isArray(data) || data.length === 0) {
        return <p>No data available</p>;
    }

    const getColor = (key) => {
        const index = labels.indexOf(key);
        return index !== -1 ? colors[index] : "#ccc";
    };
    const truncateLabel = (label) => {
        const maxLength = 6; // Adjust max length as needed
        return label.length > maxLength ? `${label.substring(0, maxLength)}...` : label;
    };


    let option = {};

    const enabledZoom = showLabelInside ? false : isScroll && isScroll === true ? isScroll : maxiMumObejectLength > 20 ? true : false;

    switch (chartType) {
        case "Doughnut Chart":
            option = {
                tooltip: { trigger: "item", position: 'top', ...toolTipConfig },
                title:{ text:charTitle , color:"Black", textStyle: { color:"#102250",fontSize:15 } },
                legend: { selected: legendSelection, show: false, icon: 'circle', orient: "horizontal", left: 'left' },
                graphic: {
                    elements: [{
                        type: 'text',
                        left: 'center',
                        top: '48%',
                        z: 999,
                        tooltip: { "show": false },
                        style: {
                            text: `${totalValue}`,
                            textAlign: 'center',
                            fontSize: 14,
                        }
                    }],
                },
                series: [
                    {
                        name: "Data",
                        type: "pie",
                        radius: ["40%", "60%"],
                        avoidLabelOverlap: true,
                        label: { show: true, position: "outside", overflow: "visible",
                            formatter: function (params) {
                            return `${Math.round(params.percent)}%`;
                          } },
                           labelLine: { show: true, length: 1 },
                        data: Object.keys(data[0])
                            .filter((key) => legendSelection?.[key])
                            .map((key) => ({
                                name: key,
                                value: data[0][key],
                                tooltipData: data[0],
                                itemStyle: { color: getColor(key) }
                            }))
                    }
                ]
            };
            break;

        case "Bar Chart":
        case "Stacked Bar Chart":
            option = {
                tooltip: { trigger: "item", ...toolTipConfig },
                title:{ text:charTitle , color:"Black",  top: "12%", textStyle: { color:"#102250",fontSize:15 } },
                legend: { icon: 'circle', orient: "horizontal", left: 'left' },
                xAxis: {
                    type: "category",
                    data: xAxisData.map(truncateLabel),
                    name: xAxisName,
                    nameLocation: 'middle',
                    nameGap: 28,
                    axisLabel: {
                        interval: 0,
                    }
                },
                yAxis: { type: "value", name: yAxisName, nameLocation: 'middle', nameGap: 35 },
                grid: { top: "23%", left: '5%', right: '1%', bottom: '20%' },
                series: labels.map((label, index) => ({
                    name: label,
                    type: "bar",
                    stack: stacked ? "total" : undefined,
                    data: data.map((item) => ({
                        value: item[label],
                        tooltipData: item.tooltipStatuus 
                    })),
                    itemStyle: { color: colors[index] },
                })),
                ...(enabledZoom && {
                    dataZoom: [
                        {
                            type: 'slider',
                            start: isScrollStartLimit || 0,
                            end: isScrollEndLimit || 30,
                            height: 15,
                            bottom:  5,
                            showDetail: false,
                            handleStyle: { color: colors[0] },
                            dataBackground: {
                                lineStyle: { color: colors[0] },
                                areaStyle: { color: 'rgba(255, 0, 0, 0.1)' }
                            },
                            ...otherZoomProperty
                        },
                        {
                            type: 'inside',
                            start: 0,
                            end: 30,
                            ...otherZoomProperty
                        }
                    ]
                })
            };
            break;

        case "Pie Chart":
            option = {
                tooltip: { trigger: "item",position: 'top',...toolTipConfig },
                title:{ text:charTitle , color:"Black", textStyle: { color:"#102250",fontSize:15 } },
                legend: { selected: legendSelection, show: false, icon: 'circle', orient: "horizontal", left: 'left' },
                graphic: {
                    elements: [{
                        type: 'text',
                        left: 'center',
                        top: '48%',
                        z: 999,
                        tooltip: { "show": false },
                        style: {
                            text: `${totalValue}`,
                            textAlign: 'center',
                            fontSize: 14,
                        }
                    }],
                },
                series: [
                    {
                        name: "Data",
                        type: "pie",
                        radius: "55%",
                        label: { show: true, position: "outside",overflow: 'visible',
                            formatter: function (params) {
                            return `${Math.round(params.percent)}%`;
                          } },                        
                          labelLine: { show: true, length: 2 },
                        labelLayout: {
                            hideOverlap: true,
                        },
                        avoidLabelOverlap: true,
                          data: Object.keys(data[0])
                        .filter((key) => legendSelection?.[key])
                        .map((key) => ({
                            name: key,
                            value: data[0][key],
                            tooltipData: data[0],
                            itemStyle: { color: getColor(key) }
                        }))
                    }
                ]
            };
            break;

        case "Line Chart":
            option = {
                tooltip: { trigger: "item" },
                title:{ text:charTitle , color:"Black", textStyle: { color:"#102250",fontSize:15 } },
                xAxis: { type: "category", data: Object.keys(data[0]) },
                yAxis: { type: "value" },
                series: Object.keys(data[0]).map((key) => ({
                    name: key,
                    type: "line",
                    data: [data[0][key]],
                    itemStyle: { color: getColor(key) }
                }))
            };
            break;

        default:
            return <p>Chart type not supported</p>;
    }
    const handleChartClick = (params) => {
        if (onChartClick) {
            onChartClick(params);
        }
    };

    const handleChartReady = () => {

    };
    const handleLegendSelectChanged = () => {
    };
    const onEvents = {
        'click': handleChartClick, // chart click event
        'legendselectchanged': handleLegendSelectChanged, // legend click event
    };
    return (
        <div>
            <ReactECharts option={option} onEvents={events ? onEvents : ""} onChartReady={handleChartReady}className={chartStyle} />
        </div>
    );
};
ChartRenderer.propTypes = {
  charTitle: PropTypes.string,
  chartStyle: PropTypes.string,
  chartType: PropTypes.string.isRequired,
  color: PropTypes.string,
  colors: PropTypes.arrayOf(PropTypes.string),
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  events: PropTypes.bool,
  isScroll: PropTypes.bool,
  isScrollEndLimit: PropTypes.number,
  isScrollStartLimit: PropTypes.number,
  labels: PropTypes.arrayOf(PropTypes.string),
  legendSelection: PropTypes.object,
  maxiMumObejectLength: PropTypes.number,
  onChartClick: PropTypes.func,
  showLabelInside: PropTypes.bool,
  stacked: PropTypes.bool,
  toolTipConfig: PropTypes.object,
  totalValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  xAxisData: PropTypes.arrayOf(PropTypes.string),
  xAxisName: PropTypes.string,
  yAxisName: PropTypes.string,
  otherZoomProperty:PropTypes.object
};

export default ChartRenderer;



